import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface RadioOption {
  value: any;
  label: string;
  disabled?: boolean;
  /** Optional explicit input id (defaults to `<name>_<index>`). */
  id?: string;
  /** Optional data-testid applied to the option label for e2e targeting. */
  testId?: string;
  /** Optional data-testid applied to the option's checkmark span. */
  checkmarkTestId?: string;
  /** Optional per-option explanation(s) rendered under the option label. */
  help?: string | string[];
  /** Optional leading icon class(es) rendered before the option label
   *  (e.g. a FontAwesome `fas fa-bicycle`). */
  icon?: string;
}

/**
 * Reusable radio group. Renders the shared `.radio-container` markup
 * (see src/scss/_inputs.scss) for a list of options.
 *
 * Works with `formControlName`, `[formControl]` and `[(ngModel)]`.
 */
@Component({
  selector: 'app-radio-group',
  templateUrl: './radio-group.component.html',
  styleUrls: ['./radio-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioGroupComponent),
      multi: true,
    },
  ],
})
export class RadioGroupComponent implements ControlValueAccessor, OnInit {
  @Input() options: RadioOption[] = [];
  @Input() label = '';
  @Input() name = '';
  /** Lay the options out horizontally (inline) instead of stacked. */
  @Input() inline = false;
  /** Grid mode: the group behaves like a Bootstrap .row (negative gutters,
   *  zero column-gap) so col-* classes in optionClass align the options with
   *  sibling grid columns. Use with [inline]="true". */
  @Input() grid = false;
  /** Extra classes for each option label (e.g. grid cols for spread demo rows). */
  @Input() optionClass = '';
  @Input() disabled = false;
  /** Detailed presentation (Setup Zones dialog): each option renders as a
   *  bold h5-style title beside the radio with its help lines as PLAIN text
   *  aligned under the title — not the italic app-explanation treatment.
   *  Mirrors develop's raw col-1 radio / col-10 h5+labels markup. */
  @Input() detailed = false;
  @Input() helpText = '';
  @Input() isInvalid = false;
  @Input() errorText = '';

  /** Emits the selected value on user change — for options with side-effects. */
  @Output() selectionChange = new EventEmitter<any>();

  /** Optional per-option rich content, rendered inside each option label after
   *  the (optional) label text. Context: the option as $implicit. */
  @ContentChild('optionContent') optionContentTpl?: TemplateRef<any>;

  value: any;

  private onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    if (!this.name) {
      this.name = 'radio-' + Math.random().toString(36).substring(2, 11);
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  select(value: any): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.selectionChange.emit(value);
  }

  /**
   * Track options by value so callers may bind `[options]` to a getter that
   * rebuilds the array each change-detection pass without the radios being
   * torn down and recreated mid-click.
   */
  trackByValue = (_index: number, option: any): any => option?.value;

  /** Normalizes RadioOption.help (string | string[]) to lines for rendering. */
  helpLines(option: RadioOption): string[] {
    return !option.help
      ? []
      : Array.isArray(option.help)
        ? option.help
        : [option.help];
  }
}
